import React, { useState, useRef } from 'react';
import { Zap, TrendingUp, AlertCircle, CheckCircle2, Loader, ExternalLink, Clock, Activity, Smartphone, Monitor } from 'lucide-react';
import apiRequest from '../services/api';
import * as i18nModule from '../services/i18n';
import { ToolLocked } from './ToolLocked';

const t = i18nModule.t || ((key: string) => key);

interface SpeedMetrics {
  totalLoadTime: number;
  domContentLoaded: number;
  pageLoadTime: number;
  serverResponseTime: number;
  dnsTime: number;
  connectionTime: number;
  resourceLoadTime: number;
  lcp?: number;
  fid?: number;
  cls?: number;
  slowResources: Array<{
    name: string;
    duration: number;
    size: number;
    type: string;
  }>;
}

interface SpeedAnalysis {
  metrics: SpeedMetrics;
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  recommendations: Array<{
    priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    issue: string;
    impact: string;
    solution: string;
    expectedImprovement: string;
  }>;
  aiInsights: {
    overallAssessment: string;
    keyFindings: string[];
    quickWins: string[];
    strategicRecommendations: string[];
  };
}

export const SpeedOptimizer: React.FC = () => {
  const [url, setUrl] = useState('');
  const [deviceType, setDeviceType] = useState<'desktop' | 'mobile'>('desktop');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<SpeedAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'recommendations' | 'resources' | 'insights'>('overview');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleAnalyze = async () => {
    if (!url.trim()) {
      setError(t('speedOptimizer.enterUrl') || 'Please enter a URL');
      return;
    }

    // Validate URL
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch {
      setError(t('speedOptimizer.invalidUrl') || 'Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
      const response = await apiRequest<{ success: boolean; data: SpeedAnalysis }>('/analysis/analyze-speed', {
        method: 'POST',
        body: JSON.stringify({ url: normalizedUrl, deviceType }),
      });

      if (response.success && response.data) {
        setAnalysisResult(response.data);
      } else {
        setError(t('speedOptimizer.analysisFailed') || 'Analysis failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Speed analysis error:', err);
      setError(err.message || t('speedOptimizer.analysisFailed') || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-emerald-400';
      case 'B': return 'text-green-400';
      case 'C': return 'text-yellow-400';
      case 'D': return 'text-orange-400';
      case 'F': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            {(() => {
              const text = t('speedOptimizer.title');
              return text && text !== 'speedOptimizer.title' ? text : 'Speed Optimizer';
            })()}
          </h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
            {(() => {
              const text = t('speedOptimizer.subtitle');
              return text && text !== 'speedOptimizer.subtitle' 
                ? text 
                : 'Analyze your website speed using browser Performance API. Get actionable recommendations to improve Core Web Vitals and page load times.';
            })()}
          </p>
        </div>

        {/* Input Form */}
        {!analysisResult && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={(() => {
                  const text = t('speedOptimizer.urlPlaceholder');
                  return text && text !== 'speedOptimizer.urlPlaceholder' 
                    ? text 
                    : 'Enter website URL (e.g., example.com)';
                })()}
                className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
              />
              <button
                onClick={handleAnalyze}
                disabled={loading || !url.trim()}
                className="px-8 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    {(() => {
                      const text = t('speedOptimizer.analyzing');
                      return text && text !== 'speedOptimizer.analyzing' ? text : 'Analyzing...';
                    })()}
                  </>
                ) : (
                  <>
                    <TrendingUp size={20} />
                    {(() => {
                      const text = t('speedOptimizer.analyzeButton');
                      return text && text !== 'speedOptimizer.analyzeButton' ? text : 'Analyze Speed';
                    })()}
                  </>
                )}
              </button>
            </div>

            {/* Device Type Selection */}
            <div className="flex items-center gap-4">
              <span className="text-slate-300 text-sm font-medium">
                {(() => {
                  const text = t('speedOptimizer.deviceType');
                  return text && text !== 'speedOptimizer.deviceType' ? text : 'Device Type:';
                })()}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeviceType('desktop')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    deviceType === 'desktop'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <Monitor size={18} />
                  {(() => {
                    const text = t('speedOptimizer.desktop');
                    return text && text !== 'speedOptimizer.desktop' ? text : 'Desktop';
                  })()}
                </button>
                <button
                  onClick={() => setDeviceType('mobile')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    deviceType === 'mobile'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <Smartphone size={18} />
                  {(() => {
                    const text = t('speedOptimizer.mobile');
                    return text && text !== 'speedOptimizer.mobile' ? text : 'Mobile';
                  })()}
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
                <AlertCircle className="text-red-400" size={20} />
                <span className="text-red-300">{error}</span>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {analysisResult && (
          <div className="space-y-6">
            {/* Score Card */}
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {(() => {
                      const text = t('speedOptimizer.performanceScore');
                      return text && text !== 'speedOptimizer.performanceScore' ? text : 'Performance Score';
                    })()}
                  </h2>
                  <div className={`text-6xl font-bold ${getGradeColor(analysisResult.grade)}`}>
                    {analysisResult.score}/100
                  </div>
                  <div className={`text-2xl font-bold ${getGradeColor(analysisResult.grade)} mt-2`}>
                    {(() => {
                      const text = t('speedOptimizer.grade');
                      return text && text !== 'speedOptimizer.grade' ? `${text}: ${analysisResult.grade}` : `Grade: ${analysisResult.grade}`;
                    })()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="text-center">
                    <div className="text-slate-400 text-sm mb-1">
                      {(() => {
                        const text = t('speedOptimizer.totalLoadTime');
                        return text && text !== 'speedOptimizer.totalLoadTime' ? text : 'Total Load Time';
                      })()}
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {formatTime(analysisResult.metrics.totalLoadTime)}
                    </div>
                  </div>
                  {analysisResult.metrics.lcp && (
                    <div className="text-center">
                      <div className="text-slate-400 text-sm mb-1">
                        {(() => {
                          const text = t('speedOptimizer.lcp');
                          return text && text !== 'speedOptimizer.lcp' ? text : 'LCP';
                        })()}
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {formatTime(analysisResult.metrics.lcp)}
                      </div>
                    </div>
                  )}
                  {analysisResult.metrics.fid && (
                    <div className="text-center">
                      <div className="text-slate-400 text-sm mb-1">
                        {(() => {
                          const text = t('speedOptimizer.fid');
                          return text && text !== 'speedOptimizer.fid' ? text : 'FID';
                        })()}
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {formatTime(analysisResult.metrics.fid)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-700 pb-4">
                {(['overview', 'recommendations', 'resources', 'insights'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      activeTab === tab
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    {(() => {
                      const tabKey = `speedOptimizer.${tab}`;
                      const text = t(tabKey);
                      return text && text !== tabKey 
                        ? text 
                        : tab.charAt(0).toUpperCase() + tab.slice(1);
                    })()}
                  </button>
                ))}
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-white mb-4">
                    {(() => {
                      const text = t('speedOptimizer.metrics');
                      return text && text !== 'speedOptimizer.metrics' ? text : 'Performance Metrics';
                    })()}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { label: (() => { const text = t('speedOptimizer.domContentLoaded'); return text && text !== 'speedOptimizer.domContentLoaded' ? text : 'DOM Content Loaded'; })(), value: analysisResult.metrics.domContentLoaded },
                      { label: (() => { const text = t('speedOptimizer.pageLoadTime'); return text && text !== 'speedOptimizer.pageLoadTime' ? text : 'Page Load Time'; })(), value: analysisResult.metrics.pageLoadTime },
                      { label: (() => { const text = t('speedOptimizer.serverResponse'); return text && text !== 'speedOptimizer.serverResponse' ? text : 'Server Response'; })(), value: analysisResult.metrics.serverResponseTime },
                      { label: (() => { const text = t('speedOptimizer.dnsTime') || t('speedOptimizer.dnsLookup'); return text && text !== 'speedOptimizer.dnsTime' && text !== 'speedOptimizer.dnsLookup' ? text : 'DNS Lookup'; })(), value: analysisResult.metrics.dnsTime },
                      { label: (() => { const text = t('speedOptimizer.connectionTime'); return text && text !== 'speedOptimizer.connectionTime' ? text : 'Connection Time'; })(), value: analysisResult.metrics.connectionTime },
                      { label: (() => { const text = t('speedOptimizer.resourceLoad'); return text && text !== 'speedOptimizer.resourceLoad' ? text : 'Resource Load'; })(), value: analysisResult.metrics.resourceLoadTime },
                    ].map((metric, index) => (
                      <div key={index} className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                        <div className="text-slate-400 text-sm mb-1">{metric.label}</div>
                        <div className="text-2xl font-bold text-white">{formatTime(metric.value)}</div>
                      </div>
                    ))}
                  </div>

                  {analysisResult.metrics.slowResources.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-lg font-bold text-white mb-4">
                        {(() => {
                          const text = t('speedOptimizer.slowResources');
                          return text && text !== 'speedOptimizer.slowResources' ? text : 'Slow Resources';
                        })()}
                      </h4>
                      <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                                {(() => {
                                  const text = t('speedOptimizer.resource');
                                  return text && text !== 'speedOptimizer.resource' ? text : 'Resource';
                                })()}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                                {(() => {
                                  const text = t('speedOptimizer.type');
                                  return text && text !== 'speedOptimizer.type' ? text : 'Type';
                                })()}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                                {(() => {
                                  const text = t('speedOptimizer.duration');
                                  return text && text !== 'speedOptimizer.duration' ? text : 'Duration';
                                })()}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                                {(() => {
                                  const text = t('speedOptimizer.size');
                                  return text && text !== 'speedOptimizer.size' ? text : 'Size';
                                })()}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {analysisResult.metrics.slowResources.slice(0, 10).map((resource, index) => (
                              <tr key={index} className="border-t border-slate-700">
                                <td className="px-4 py-3 text-slate-300 text-sm truncate max-w-xs">
                                  {resource.name}
                                </td>
                                <td className="px-4 py-3 text-slate-300">{resource.type}</td>
                                <td className="px-4 py-3 text-slate-300">{formatTime(resource.duration)}</td>
                                <td className="px-4 py-3 text-slate-300">{formatSize(resource.size)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recommendations Tab */}
              {activeTab === 'recommendations' && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white mb-4">
                    {(() => {
                      const text = t('speedOptimizer.recommendations');
                      return text && text !== 'speedOptimizer.recommendations' ? text : 'Optimization Recommendations';
                    })()}
                  </h3>
                  {analysisResult.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="bg-slate-900/50 border border-slate-700 rounded-xl p-6"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                            rec.priority === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                            rec.priority === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
                            rec.priority === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {rec.priority}
                          </span>
                          <h4 className="text-lg font-bold text-white">{rec.issue}</h4>
                        </div>
                      </div>
                      <p className="text-slate-300 mb-2">
                        <strong className="text-white">{t('speedOptimizer.impact') || 'Impact'}:</strong> {rec.impact}
                      </p>
                      <p className="text-slate-300 mb-3">
                        <strong className="text-white">{t('speedOptimizer.solution') || 'Solution'}:</strong> {rec.solution}
                      </p>
                      <p className="text-emerald-400 font-bold">
                        <strong>{t('speedOptimizer.expectedImprovement') || 'Expected Improvement'}:</strong> {rec.expectedImprovement}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Resources Tab */}
              {activeTab === 'resources' && analysisResult.metrics.slowResources.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {(() => {
                      const text = t('speedOptimizer.allResources');
                      return text && text !== 'speedOptimizer.allResources' ? text : 'All Resources';
                    })()}
                  </h3>
                  <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                            {(() => {
                              const text = t('speedOptimizer.resource');
                              return text && text !== 'speedOptimizer.resource' ? text : 'Resource';
                            })()}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                            {(() => {
                              const text = t('speedOptimizer.type');
                              return text && text !== 'speedOptimizer.type' ? text : 'Type';
                            })()}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                            {(() => {
                              const text = t('speedOptimizer.duration');
                              return text && text !== 'speedOptimizer.duration' ? text : 'Duration';
                            })()}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-300">
                            {(() => {
                              const text = t('speedOptimizer.size');
                              return text && text !== 'speedOptimizer.size' ? text : 'Size';
                            })()}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisResult.metrics.slowResources.map((resource, index) => (
                          <tr key={index} className="border-t border-slate-700">
                            <td className="px-4 py-3 text-slate-300 text-sm truncate max-w-xs">
                              {resource.name}
                            </td>
                            <td className="px-4 py-3 text-slate-300">{resource.type}</td>
                            <td className="px-4 py-3 text-slate-300">{formatTime(resource.duration)}</td>
                            <td className="px-4 py-3 text-slate-300">{formatSize(resource.size)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Insights Tab */}
              {activeTab === 'insights' && analysisResult.aiInsights && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-white mb-4">
                    {(() => {
                      const text = t('speedOptimizer.aiInsights');
                      return text && text !== 'speedOptimizer.aiInsights' ? text : 'AI-Powered Insights';
                    })()}
                  </h3>
                  
                  {analysisResult.aiInsights.overallAssessment && (
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                      <h4 className="text-lg font-bold text-white mb-3">
                        {(() => {
                          const text = t('speedOptimizer.overallAssessment');
                          return text && text !== 'speedOptimizer.overallAssessment' ? text : 'Overall Assessment';
                        })()}
                      </h4>
                      <p className="text-slate-300">{analysisResult.aiInsights.overallAssessment}</p>
                    </div>
                  )}

                  {analysisResult.aiInsights.keyFindings && analysisResult.aiInsights.keyFindings.length > 0 && (
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                      <h4 className="text-lg font-bold text-white mb-3">
                        {(() => {
                          const text = t('speedOptimizer.keyFindings');
                          return text && text !== 'speedOptimizer.keyFindings' ? text : 'Key Findings';
                        })()}
                      </h4>
                      <ul className="space-y-2 text-slate-300">
                        {analysisResult.aiInsights.keyFindings.map((finding, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-indigo-400 mt-1">•</span>
                            <span>{finding}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {analysisResult.aiInsights.quickWins && analysisResult.aiInsights.quickWins.length > 0 && (
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                      <h4 className="text-lg font-bold text-white mb-3">
                        {(() => {
                          const text = t('speedOptimizer.quickWins');
                          return text && text !== 'speedOptimizer.quickWins' ? text : 'Quick Wins';
                        })()}
                      </h4>
                      <ul className="space-y-2 text-slate-300">
                        {analysisResult.aiInsights.quickWins.map((win, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-emerald-400 mt-1">⚡</span>
                            <span>{win}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {analysisResult.aiInsights.strategicRecommendations && analysisResult.aiInsights.strategicRecommendations.length > 0 && (
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                      <h4 className="text-lg font-bold text-white mb-3">
                        {(() => {
                          const text = t('speedOptimizer.strategicRecommendations');
                          return text && text !== 'speedOptimizer.strategicRecommendations' ? text : 'Strategic Recommendations';
                        })()}
                      </h4>
                      <ol className="space-y-2 text-slate-300 list-decimal list-inside">
                        {analysisResult.aiInsights.strategicRecommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* New Analysis Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setAnalysisResult(null);
                  setUrl('');
                  setError(null);
                }}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-medium transition-all"
              >
                {(() => {
                  const text = t('speedOptimizer.newAnalysis');
                  return text && text !== 'speedOptimizer.newAnalysis' ? text : 'New Analysis';
                })()}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

